import { Node as PmNode } from 'prosemirror-model'
import { EditorState } from 'prosemirror-state'
import { DecorationSet } from 'prosemirror-view'

import { NodeTypeMap } from '@showrunner/codex'

import { MoresContds } from '#util/formats'

import { actSlugTrackerKey } from '../act-slug-tracker'
import { getSpeaker } from '../block-info/speakerLookup'
import { ScriptContext } from '../inlinePageBreaks/inlinePageBreaksPlugin'
import {
  BoundaryKind,
  buildPaginationDecorations,
  buildRevisionRailDecorations,
  type PageDecorationBuildOptions,
  type PageDecorationData,
} from '../page-decoration'
import { highestRevisionForRange, getLoadedRevisionsState } from '../revisions'

import { PageLockMap } from './PageLockMap'
import { ScriptPagination } from './paginationPlugin'
import { PageBreakType } from './Paginator'
import {
  findSceneContinuations,
  SceneContinuationInfo,
} from './scene-continuations'

export function buildPageDecorationData(
  editorState: EditorState,
  paginationStates: ScriptPagination,
  pageLocking: PageLockMap,
  moresContds?: MoresContds,
): PageDecorationData[] {
  const actSlugTracker = actSlugTrackerKey.getState(editorState)
  const isDialogueMoreContd =
    moresContds?.enableDialogueMores && moresContds?.enableDialogueContds

  const pages: PageDecorationData[] = []
  let titlePageNumber = 1
  let bodyPageNumber = 1
  let pageIndex = 0
  let sceneContinuations: SceneContinuationInfo[] = []

  if (actSlugTracker) {
    // Working with the iterator when we just have to turn around and make it an
    // array is a little cumbersome
    const paginationPages = Array.from(paginationStates).flatMap(
      (paginationState) => paginationState.pages,
    )
    sceneContinuations = findSceneContinuations(paginationPages, (pos) =>
      actSlugTracker.getSceneAt(pos),
    )
  }

  for (const paginationState of paginationStates) {
    for (let i = 0; i < paginationState.pages.length; i++) {
      const page = paginationState.pages[i]
      const nextPage = paginationState.pages[i + 1]
      const sceneContinuation = sceneContinuations[pageIndex]

      if (paginationState.isTitlePage) {
        pages.push({
          start: page.start,
          end: page.end,
          shortfall: page.shortfall,
          boundaryKind: BoundaryKind.Normal,
          pageNumber: titlePageNumber,
          isLockedPageBreak: false,
          isTitlePage: true,
        })
        titlePageNumber += 1
        pageIndex += 1
        continue
      }

      const lockingEntry = pageLocking.getEntry(bodyPageNumber)
      const { act, slug } = actSlugTracker?.getActAndSlugAt(page.end - 1) ?? {}
      const latestRevision = highestRevisionForRange(
        editorState,
        page.start,
        page.end,
      )

      let pageSection = ''
      if (act) pageSection = act.toUpperCase()
      if (slug) pageSection = `${pageSection} – ${slug}`

      let boundaryKind = BoundaryKind.Normal
      if (page.breakType === PageBreakType.DialogueContinuation) {
        boundaryKind = BoundaryKind.DialogueContinuation
      } else if (
        nextPage &&
        editorState.doc.resolve(page.end).parent.isTextblock
      ) {
        boundaryKind = BoundaryKind.InlineSplit
      } else if (
        isDialogueMoreContd &&
        nextPage &&
        isDialogueOrParenthetical(page.endBlock) &&
        isDialogueOrParenthetical(nextPage.startBlock)
      ) {
        boundaryKind = BoundaryKind.AdjacentDialogues
      }

      pages.push({
        start: page.start,
        end: page.end,
        // for last page use shortflass 0
        shortfall: nextPage ? page.shortfall : 0,
        boundaryKind,
        pageNumber: bodyPageNumber,
        pageAlias: lockingEntry?.alias,
        isLockedPageBreak: lockingEntry?.isLocked ?? false,
        pageSection,
        latestRevision,
        isManualBreak: page.breakType === PageBreakType.ManualBreak,
        sceneContdHeader: sceneContinuation?.sceneContinuesFromPrevious,
        sceneContdFooter: sceneContinuation?.sceneContinuesToNext,
        sceneNumber: sceneContinuation?.sceneNumber,
        sceneElementNumber: sceneContinuation?.sceneElementNumber,
      })
      bodyPageNumber += 1
      pageIndex += 1
    }
  }

  return pages
}

export function buildDecorationsFromPageDecoration(
  paginationStates: ScriptPagination,
  pageLocking: PageLockMap,
  editorState: EditorState,
  scriptContext?: ScriptContext,
  moresContds?: MoresContds,
): DecorationSet {
  const pageData = buildPageDecorationData(
    editorState,
    paginationStates,
    pageLocking,
    moresContds,
  )
  const titlePageCount = pageData.filter((p) => p.isTitlePage).length
  const revisionsState = getLoadedRevisionsState(editorState)

  const buildOptions: PageDecorationBuildOptions = {
    pageMeta: scriptContext?.pageMeta,
    moresContds,
    totalPages: pageData.length - titlePageCount,
    scriptMeta: {
      title: scriptContext?.scriptTitle,
      timestamp: scriptContext?.timestamp,
      date: scriptContext?.date,
      duration: scriptContext?.duration,
      activeRevision: revisionsState?.revisions.active,
    },
    callbacks: {
      getSpeaker: (pos) => getSpeaker(editorState, pos)?.toUpperCase(),
    },
  }

  let decorations = buildPaginationDecorations(
    editorState.doc,
    pageData,
    buildOptions,
  )

  if (revisionsState?.showPageColors) {
    decorations = decorations.add(
      editorState.doc,
      buildRevisionRailDecorations(pageData),
    )
  }

  return decorations
}

function isDialogueOrParenthetical(node: PmNode): boolean {
  return (
    node.type.name === NodeTypeMap.DIALOGUE ||
    node.type.name === NodeTypeMap.PARENTHETICAL
  )
}
